import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import iataProfileService from '../services/iataProfileService';
import './ProfileIATAPage.css';

const ProfileIATAPage = () => {
    const navigate = useNavigate();
    const { ppId } = useParams();
    const [search, setSearch] = useState('');
    const [profiles, setProfiles] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => { loadProfiles(); }, [ppId]); // eslint-disable-line

    const loadProfiles = async () => {
        setLoading(true);
        try {
            const res = await iataProfileService.list(ppId);
            setProfiles(res.data?.profiles || []);
        } catch (err) {
            console.error('Failed to load IATA profiles', err);
        } finally {
            setLoading(false);
        }
    };

    const filtered = profiles.filter(p =>
        !search ||
        (p.name || '').toLowerCase().includes(search.toLowerCase()) ||
        (p.corporate_id || '').toLowerCase().includes(search.toLowerCase())
    );

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this profile?')) return;
        try {
            await iataProfileService.remove(ppId, id);
            setProfiles(prev => prev.filter(p => p.id !== id));
        } catch (err) {
            console.error('Failed to delete profile', err);
        }
    };

    const ProfileIcon = () => (
        <div className="pi-avatar">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <circle cx="9" cy="7" r="3" stroke="#7c3aed" strokeWidth="1.75"/>
                <circle cx="17" cy="7" r="3" stroke="#7c3aed" strokeWidth="1.75"/>
                <path d="M3 19c0-3 2.5-5 6-5" stroke="#7c3aed" strokeWidth="1.75" strokeLinecap="round"/>
                <path d="M11 19c0-3 2.5-5 6-5s6 2 6 5" stroke="#7c3aed" strokeWidth="1.75" strokeLinecap="round"/>
            </svg>
        </div>
    );

    return (
        <div className="pi-page">
            {/* Header */}
            <div className="pi-header">
                <div className="pi-header-left">
                    <button className="pi-back-btn" onClick={() => navigate(`/apps/payment-porter/${ppId}`)}>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path d="M10 3L5 8l5 5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        Back to Payment Porter
                    </button>
                    <div className="pi-title-block">
                        <h1 className="pi-title">Profile IATA Configuration</h1>
                        <p className="pi-subtitle">Configure Corporate ID and IATA number filtering for reservation processing</p>
                    </div>
                </div>
                <div className="pi-header-actions">
                    <button className="pi-btn-add" onClick={() => navigate(`/apps/payment-porter/${ppId}/profile-iata/new`)}>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                        Add Profile
                    </button>
                </div>
            </div>

            {/* Search */}
            <div className="pi-search-wrap">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="pi-search-icon">
                    <circle cx="7" cy="7" r="4.5" stroke="#9ca3af" strokeWidth="1.5"/>
                    <path d="M11 11l3 3" stroke="#9ca3af" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
                <input
                    type="text"
                    className="pi-search-input"
                    placeholder="Search profiles by name or Corporate ID/IATA..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                />
            </div>

            {/* List panel */}
            <div className="pi-list-panel">
                {filtered.length === 0 ? (
                    <div className="pi-empty">
                        <div className="pi-empty-icon">
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                                <circle cx="9" cy="7" r="3" stroke="#d1d5db" strokeWidth="1.5"/>
                                <circle cx="17" cy="7" r="3" stroke="#d1d5db" strokeWidth="1.5"/>
                                <path d="M3 19c0-3 2.5-5 6-5" stroke="#d1d5db" strokeWidth="1.5" strokeLinecap="round"/>
                                <path d="M11 19c0-3 2.5-5 6-5s6 2 6 5" stroke="#d1d5db" strokeWidth="1.5" strokeLinecap="round"/>
                            </svg>
                        </div>
                        <div className="pi-empty-title">
                            {search ? 'No profiles match your search' : 'No IATA profiles configured'}
                        </div>
                        <div className="pi-empty-sub">
                            {search ? 'Try a different search term.' : 'Get started by adding your first IATA profile.'}
                        </div>
                    </div>
                ) : (
                    <table className="pi-table">
                        <thead>
                            <tr>
                                <th>Profile</th>
                                <th>Corporate ID / IATA</th>
                                <th>Location</th>
                                <th>Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map(p => (
                                <tr key={p.id}>
                                    <td>
                                        <div className="pi-profile-cell">
                                            <ProfileIcon />
                                            <span className="pi-profile-name">{p.name}</span>
                                        </div>
                                    </td>
                                    <td className="pi-id-cell">{p.corporate_id}</td>
                                    <td className="pi-location-cell">{p.city || <span className="pi-no-value">—</span>}</td>
                                    <td>
                                        <span className={p.active !== false ? 'pi-badge-active' : 'pi-badge-inactive'}>
                                            {p.active !== false ? 'Active' : 'Inactive'}
                                        </span>
                                    </td>
                                    <td>
                                        <div className="pi-actions">
                                            <button
                                                className="pi-btn-edit"
                                                onClick={() => navigate(`/apps/payment-porter/${ppId}/profile-iata/${p.id}/edit`)}
                                                title="Edit"
                                            >
                                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                                                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                </svg>
                                            </button>
                                            <button
                                                className="pi-btn-delete"
                                                onClick={() => handleDelete(p.id)}
                                                title="Delete"
                                            >
                                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                                                    <polyline points="3 6 5 6 21 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                    <path d="M10 11v6M14 11v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                                    <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                </svg>
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};

export default ProfileIATAPage;
